import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Collapse
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountTree as WorkflowIcon,
  Psychology as AgentsIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Folder as FolderIcon,
  Star as StarIcon,
  History as HistoryIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

interface FlowNavigationProps {
  open: boolean;
  onToggle: () => void;
  width?: number;
  collapsedWidth?: number;
}

const FlowNavigation: React.FC<FlowNavigationProps> = ({ 
  open, 
  onToggle, 
  width = 280,
  collapsedWidth = 64
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth0();

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin + '/login' } });
  };

  const navigationItems = [
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      description: 'Overview and analytics'
    },
    {
      title: 'Workflows',
      icon: <WorkflowIcon />,
      path: '/flow-editor',
      description: 'Create and manage workflows'
    },
    {
      title: 'Agents',
      icon: <AgentsIcon />,
      path: '/agents',
      description: 'Manage AI agents'
    },
    {
      title: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
      description: 'Application settings'
    }
  ];

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={true}
      sx={{
        width: open ? width : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? width : collapsedWidth,
          boxSizing: 'border-box',
          border: 'none',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          transition: 'width 0.3s ease'
        }
      }}
    >
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        {/* Header */}
        <Box sx={{ 
          p: open ? 3 : 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: open ? 'space-between' : 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          minHeight: 80
        }}>
          {open ? (
            <>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Workflow Editor
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Visual Workflow Builder
                </Typography>
              </Box>
              <Tooltip title="Collapse">
                <IconButton 
                  onClick={onToggle}
                  sx={{ 
                    color: 'white',
                    '&:hover': { 
                      background: 'rgba(255,255,255,0.1)' 
                    }
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Expand" placement="right">
              <IconButton 
                onClick={onToggle}
                sx={{ 
                  color: 'white',
                  '&:hover': { 
                    background: 'rgba(255,255,255,0.1)' 
                  }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Navigation Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          background: 'white',
          color: 'text.primary',
          display: open ? 'block' : 'none'
        }}>
          <List sx={{ pt: 2 }}>
            {navigationItems.map((item, index) => (
              <Box key={item.title}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      mb: 0.5,
                      background: isActive(item.path) 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                        : 'transparent',
                      color: isActive(item.path) ? 'white' : 'inherit',
                      '&:hover': {
                        background: isActive(item.path)
                          ? 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
                          : 'rgba(102, 126, 234, 0.08)'
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: isActive(item.path) ? 'white' : 'primary.main',
                      minWidth: 40
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.title}
                      secondary={item.description}
                      primaryTypographyProps={{
                        fontWeight: isActive(item.path) ? 600 : 500
                      }}
                      secondaryTypographyProps={{
                        color: isActive(item.path) ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </Box>
            ))}
          </List>
        </Box>

        {/* Collapsed Navigation Icons */}
        {!open && (
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            pt: 2,
            gap: 1,
            background: 'white'
          }}>
            {navigationItems.map((item) => (
              <Tooltip key={item.title} title={item.title} placement="right">
                <IconButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: isActive(item.path) 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                      : 'transparent',
                    color: isActive(item.path) ? 'white' : 'primary.main',
                    '&:hover': {
                      background: isActive(item.path)
                        ? 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
                        : 'rgba(102, 126, 234, 0.08)'
                    }
                  }}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ 
          p: open ? 2 : 1, 
          borderTop: '1px solid #e5e7eb',
          background: '#f8fafc'
        }}>
          {open ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <SettingsIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <HelpIcon fontSize="small" />
              </IconButton>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Logout" placement="top">
                <IconButton 
                  size="small" 
                  sx={{ color: 'text.secondary' }}
                  onClick={handleLogout}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Chip 
                label="v1.0.0" 
                size="small" 
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            </Stack>
          ) : (
            <Stack direction="column" spacing={1} alignItems="center">
              <Tooltip title="Settings" placement="right">
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Help" placement="right">
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Logout" placement="right">
                <IconButton 
                  size="small" 
                  sx={{ color: 'text.secondary' }}
                  onClick={handleLogout}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default FlowNavigation; 